/**
 * Provider abstraction (spec sections 7, 8).
 *
 * The rendering engine must never depend on Google, or on any vendor.
 * Everything vendor-specific lives behind this interface, and the
 * interface is honest about what a provider can and cannot do.
 */

import { ProviderUnavailableError } from '../core/errors.js';

/** @typedef {'automated'|'manual'|'local'} ProviderType */

/**
 * @typedef {object} ProviderCapabilities
 * @property {ProviderType} type
 * @property {boolean} automated            can it generate without a human?
 * @property {boolean} requiresApiKey
 * @property {boolean} requiresWorker       must calls be proxied to keep a secret off the client?
 * @property {boolean} manualStepRequired   a human must do work outside the app
 * @property {boolean} asynchronous         generation returns an operation to poll
 * @property {boolean} supportsCancel
 * @property {boolean} nativeAudio
 * @property {string[]} durations
 * @property {string[]} aspectRatios
 * @property {string[]} resolutions
 * @property {'per-second'|'free-tier'|'none'|'unknown'} costModel
 * @property {string|null} costNote
 * @property {boolean} browserAutomation    always false - see SECURITY.md
 */

export const PROVIDER_STATUS = {
  READY: 'ready',
  UNCONFIGURED: 'unconfigured',
  UNAVAILABLE: 'unavailable',
  ERROR: 'error',
  MANUAL: 'manual',
};

export class VideoProvider {
  /**
   * @param {object} definition
   * @param {string} definition.id
   * @param {string} definition.name
   * @param {ProviderType} definition.type
   * @param {ProviderCapabilities} definition.capabilities
   * @param {string} definition.description
   * @param {string|null} [definition.docsUrl]
   */
  constructor(definition) {
    this.id = definition.id;
    this.name = definition.name;
    this.type = definition.type;
    this.description = definition.description;
    this.docsUrl = definition.docsUrl ?? null;
    this.capabilities = definition.capabilities;
  }

  getCapabilities() {
    return { ...this.capabilities };
  }

  /** @returns {Promise<{state:string, detail?:string, checkedAt?:string, meta?:object}>} */
  async status() {
    return { state: PROVIDER_STATUS.READY, checkedAt: new Date().toISOString() };
  }

  /** @returns {Promise<{configured:boolean, detail?:string, meta?:object}>} */
  async validateCredentials() {
    return { configured: !this.capabilities.requiresApiKey, detail: 'No credentials required.' };
  }

  async generate() {
    throw new ProviderUnavailableError(`${this.name} cannot generate through an API.`);
  }

  async getGenerationStatus() {
    throw new ProviderUnavailableError(`${this.name} does not expose generation status.`);
  }

  async downloadResult() {
    throw new ProviderUnavailableError(`${this.name} cannot download results programmatically.`);
  }

  /** Best-effort. Providers without a documented cancel endpoint say so. */
  async cancel() {
    return { cancelled: false, detail: `${this.name} has no documented cancellation endpoint.` };
  }
}

/** Cost estimate helper shared by the automated providers. */
export function estimateCost(pricePerSecond, resolution, seconds) {
  if (!pricePerSecond) return null;
  const rate = pricePerSecond[resolution] ?? Object.values(pricePerSecond)[0];
  if (!Number.isFinite(rate)) return null;
  return Math.round(rate * seconds * 1000) / 1000;
}
